import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, TrendingUp, Target, BarChart3, Loader2, 
  CheckCircle2, XCircle, AlertCircle, PieChart, History,
  ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format currency
const formatCurrency = (value, decimals = 2) => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

export default function PerformanceAnalytics() {
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [timeData, setTimeData] = useState([]);
  const [tradeSummary, setTradeSummary] = useState(null);

  const fetchPerformance = async () => {
    try {
      setLoading(true);
      
      // Fetch from trade-history endpoint (same as History page for consistency)
      const historyRes = await axios.get(`${BACKEND_URL}/api/ai/trade-history?days=3650`); // All history
      
      const trades = historyRes.data.trades || [];
      
      // Group trades by date for the time series chart
      const tradesByDate = {};
      trades.forEach(trade => {
        const date = trade.evaluation_date || trade.trade_date;
        if (!date) return;
        
        if (!tradesByDate[date]) {
          tradesByDate[date] = {
            date,
            successful: 0,
            unsuccessful: 0,
            total: 0
          };
        }
        
        tradesByDate[date].total++;
        if (trade.evaluation_result === 'WIN') {
          tradesByDate[date].successful++;
        } else {
          tradesByDate[date].unsuccessful++;
        }
      });
      
      // Convert to array and sort by date
      const timeSeriesData = Object.values(tradesByDate)
        .sort((a, b) => a.date.localeCompare(b.date));
      
      // Group by strategy (BUY/SHORT)
      const byStrategy = {};
      trades.forEach(trade => {
        const strategy = trade.strategy || 'BUY';
        if (!byStrategy[strategy]) {
          byStrategy[strategy] = { strategy, total: 0, successful: 0, unsuccessful: 0 };
        }
        byStrategy[strategy].total++;
        if (trade.evaluation_result === 'WIN') {
          byStrategy[strategy].successful++;
        } else {
          byStrategy[strategy].unsuccessful++;
        }
      });
      const strategyArray = Object.values(byStrategy).map(s => ({
        sentiment: s.strategy,
        total_trades: s.total,
        accuracy: s.total > 0 ? ((s.successful / s.total) * 100).toFixed(1) : 0,
      }));
      
      // Group by sector
      const bySector = {};
      trades.forEach(trade => {
        const sector = trade.sector || 'Unknown';
        if (!bySector[sector]) {
          bySector[sector] = { sector, total: 0, successful: 0, unsuccessful: 0 };
        }
        bySector[sector].total++;
        if (trade.evaluation_result === 'WIN') {
          bySector[sector].successful++;
        } else {
          bySector[sector].unsuccessful++;
        }
      });
      const sectorArray = Object.values(bySector)
        .map(s => ({
          sector: s.sector,
          total: s.total,
          success_rate: s.total > 0 ? ((s.successful / s.total) * 100).toFixed(1) : 0,
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 10); // Top 10 sectors
      
      // Calculate target hit statistics
      const targetStats = {
        target_hit: 0,        // Reached or exceeded predicted price
        partial_target: 0,    // Moved in right direction but didn't hit target
        wrong_direction: 0    // Moved opposite direction
      };
      
      trades.forEach(trade => {
        const start = trade.start_price || 0;
        const actual = trade.actual_price || 0;
        const predicted = trade.predicted_price || trade.target_price || 0;
        const strategy = trade.strategy || 'BUY';
        
        if (!start || !actual) return;
        
        if (strategy === 'BUY') {
          if (actual >= predicted) {
            targetStats.target_hit++; // Hit or exceeded target
          } else if (actual > start) {
            targetStats.partial_target++; // Went up but didn't hit target
          } else {
            targetStats.wrong_direction++; // Went down instead of up
          }
        } else { // SHORT
          if (actual <= predicted) {
            targetStats.target_hit++; // Hit or exceeded target (went down)
          } else if (actual < start) {
            targetStats.partial_target++; // Went down but didn't hit target
          } else {
            targetStats.wrong_direction++; // Went up instead of down
          }
        }
      });
      
      // Group by confidence/ranking score ranges
      const byConfidence = [
        { range: 'High (70-100)', count: 0, successful: 0 },
        { range: 'Medium (50-70)', count: 0, successful: 0 },
        { range: 'Low (0-50)', count: 0, successful: 0 },
      ];
      
      trades.forEach(trade => {
        const score = trade.probability_score || trade.ranking_score || trade.confidence || 0;
        const isWin = trade.evaluation_result === 'WIN';
        
        if (score >= 70) {
          byConfidence[0].count++;
          if (isWin) byConfidence[0].successful++;
        } else if (score >= 50) {
          byConfidence[1].count++;
          if (isWin) byConfidence[1].successful++;
        } else {
          byConfidence[2].count++;
          if (isWin) byConfidence[2].successful++;
        }
      });
      
      const confidenceArray = byConfidence
        .filter(c => c.count > 0)
        .map(c => ({
          confidence_level: c.range,
          total_trades: c.count,
          accuracy: c.count > 0 ? ((c.successful / c.count) * 100).toFixed(1) : 0,
        }));
      
      // Transform to match expected format
      const transformedOverview = {
        overall: {
          total_trades: historyRes.data.total || 0,
          total_predictions: historyRes.data.total || 0,
          successful_trades: historyRes.data.successful || 0,
          failed_trades: historyRes.data.not_successful || 0,
          success_rate: historyRes.data.successful_pct || 0,
          failure_rate: historyRes.data.not_successful_pct || 0,
        },
        by_sentiment: strategyArray,
        by_confidence: confidenceArray
      };
      
      const transformedSummary = {
        by_result: {
          WIN: historyRes.data.successful || 0,
          LOSS: historyRes.data.not_successful || 0,
          target_hit: targetStats.target_hit,
          partial_target: targetStats.partial_target,
          wrong_direction: targetStats.wrong_direction,
        },
        by_sector: sectorArray,
        avg_return: 0
      };
      
      setOverview(transformedOverview);
      setTimeData(timeSeriesData);
      setTradeSummary(transformedSummary);
    } catch (err) {
      console.error("Performance fetch error:", err);
      toast.error("Failed to fetch performance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformance();
  }, []);

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-[hsl(var(--primary))]" />
            <p className="text-muted-foreground mt-2">Loading performance data...</p>
          </div>
        </div>
      </div>
    );
  }

  const overall = overview?.overall || {};
  const byStrategy = overview?.by_sentiment || [];
  const byConfidence = overview?.by_confidence || [];
  const resultStats = tradeSummary?.by_result || {};
  const sectorStats = tradeSummary?.by_sector || [];

  const totalTrades = overall.total_trades || overall.total_predictions || 0;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link to="/">
              <Button variant="ghost" size="sm" className="mb-2">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-4xl font-bold">Performance Analytics</h1>
            <p className="text-muted-foreground mt-1">
              Analysis of {totalTrades} evaluated trades from Trade History
            </p>
          </div>
          <Button onClick={fetchPerformance} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>

        {totalTrades === 0 ? (
          <Card className="p-8 text-center">
            <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <h3 className="text-xl font-semibold mb-2">No Performance Data Yet</h3>
            <p className="text-muted-foreground mb-4">
              Performance metrics will appear once trades are evaluated.
            </p>
            <p className="text-sm text-muted-foreground">
              The evaluation runs every 15 minutes during market hours (9:15 AM - 3:30 PM IST).
            </p>
            <Link to="/ai-history" className="mt-4 inline-block">
              <Button variant="outline" className="gap-2">
                <History className="h-4 w-4" />
                View Trade History
              </Button>
            </Link>
          </Card>
        ) : (
          <>
            {/* Key Metrics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4" data-testid="total-trades-card">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-muted-foreground text-sm">Total Trades</p>
                  <BarChart3 className="h-5 w-5 text-[hsl(var(--primary))]" />
                </div>
                <p className="text-3xl font-bold">{totalTrades}</p>
                <p className="text-xs text-muted-foreground mt-1">Evaluated predictions</p>
              </Card>

              <Card className="p-4" data-testid="success-rate-card">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-muted-foreground text-sm">Successful</p>
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                </div>
                <p className="text-3xl font-bold text-emerald-400">{overall.success_rate || 0}%</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {overall.successful_trades || 0} profitable trades
                </p>
              </Card>

              <Card className="p-4" data-testid="failure-rate-card">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-muted-foreground text-sm">Not Successful</p>
                  <XCircle className="h-5 w-5 text-red-400" />
                </div>
                <p className="text-3xl font-bold text-red-400">{overall.failure_rate || 0}%</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {overall.failed_trades || 0} not profitable
                </p>
              </Card>

              <Card className="p-4" data-testid="avg-return-card">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-muted-foreground text-sm">Overall Return</p>
                  <TrendingUp className="h-5 w-5 text-[hsl(var(--primary))]" />
                </div>
                <p className={`text-3xl font-bold ${(tradeSummary?.avg_return || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {(tradeSummary?.avg_return || 0) >= 0 ? '+' : ''}{tradeSummary?.avg_return || 0}%
                </p>
                <p className="text-xs text-muted-foreground mt-1">Portfolio return</p>
              </Card>
            </div>

            {/* Trades by Date */}
            {timeData.length > 0 && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold">Successful & Unsuccessful Trades by Date</h3>
                    <p className="text-sm text-muted-foreground mt-1">Daily breakdown of trade outcomes</p>
                  </div>
                  <BarChart3 className="h-5 w-5 text-[hsl(var(--primary))]" />
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Total</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Successful</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Unsuccessful</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Success Rate</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeData.map((day, idx) => {
                        const successRate = day.total > 0 ? ((day.successful / day.total) * 100).toFixed(1) : 0;
                        return (
                          <tr key={day.date} className="border-b border-border/50 hover:bg-accent/50 transition-colors">
                            <td className="py-3 px-4 font-medium">{day.date}</td>
                            <td className="py-3 px-4 text-center">{day.total}</td>
                            <td className="py-3 px-4 text-center">
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/20 text-emerald-400 text-sm font-medium">
                                <CheckCircle2 className="h-3 w-3" />
                                {day.successful}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/20 text-red-400 text-sm font-medium">
                                <XCircle className="h-3 w-3" />
                                {day.unsuccessful}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`font-semibold ${successRate >= 50 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {successRate}%
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              {successRate >= 50 ? (
                                <ArrowUpRight className="h-4 w-4 text-emerald-400 inline" />
                              ) : (
                                <ArrowDownRight className="h-4 w-4 text-red-400 inline" />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-border font-bold">
                        <td className="py-3 px-4">Total</td>
                        <td className="py-3 px-4 text-center">{totalTrades}</td>
                        <td className="py-3 px-4 text-center text-emerald-400">{overall.successful_trades || 0}</td>
                        <td className="py-3 px-4 text-center text-red-400">{overall.failed_trades || 0}</td>
                        <td className="py-3 px-4 text-center text-[hsl(var(--primary))]">{overall.success_rate || 0}%</td>
                        <td className="py-3 px-4"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>
            )}


            {/* Evaluation Results */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4" data-testid="target-hit-card">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-emerald-500/20">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Target Reached</p>
                    <p className="text-2xl font-bold">{resultStats.target_hit || 0}</p>
                    <p className="text-xs text-muted-foreground">Predicted price achieved</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4" data-testid="partial-target-card">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-amber-500/20">
                    <AlertCircle className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Target Partially Reached</p>
                    <p className="text-2xl font-bold">{resultStats.partial_target || 0}</p>
                    <p className="text-xs text-muted-foreground">Moved in right direction</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4" data-testid="wrong-direction-card">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-red-500/20">
                    <XCircle className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Target Went Other Direction</p>
                    <p className="text-2xl font-bold">{resultStats.wrong_direction || 0}</p>
                    <p className="text-xs text-muted-foreground">Moved opposite to prediction</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Strategy and Confidence Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6" data-testid="strategy-breakdown">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <ArrowUpRight className="h-5 w-5 text-[hsl(var(--primary))]" />
                  Accuracy by Strategy
                </h2>
                <div className="space-y-3">
                  {byStrategy.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No strategy data available</p>
                  ) : (
                    byStrategy.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-[hsl(var(--surface))] rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge className={item.sentiment === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                            {item.sentiment}
                          </Badge>
                          <span className="text-sm text-muted-foreground">{item.total_trades} trades</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-[hsl(var(--border))] rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${item.sentiment === 'BUY' ? 'bg-emerald-400' : 'bg-red-400'}`}
                              style={{ width: `${item.accuracy}%` }}
                            />
                          </div>
                          <span className="font-bold text-sm w-12 text-right">{item.accuracy}%</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>

              <Card className="p-6" data-testid="confidence-breakdown">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-[hsl(var(--primary))]" />
                  Accuracy by Ranking Score
                </h2>
                <div className="space-y-3">
                  {byConfidence.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No ranking data available</p>
                  ) : (
                    byConfidence.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-[hsl(var(--surface))] rounded-lg">
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{item.confidence_level}</p>
                          <p className="text-xs text-muted-foreground">{item.total_trades} trades</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-[hsl(var(--border))] rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full bg-[hsl(var(--primary))]"
                              style={{ width: `${item.accuracy}%` }}
                            />
                          </div>
                          <span className="font-bold text-sm w-12 text-right">{item.accuracy}%</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>

            {/* Sector Performance */}
            {sectorStats.length > 0 && (
              <Card className="p-6" data-testid="sector-performance">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-[hsl(var(--primary))]" />
                  Performance by Sector
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {sectorStats.map((item, idx) => (
                    <div key={idx} className="p-3 bg-[hsl(var(--surface))] rounded-lg">
                      <p className="font-semibold text-sm truncate">{item.sector}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground">{item.total} trades</span>
                        <Badge className={item.success_rate >= 50 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                          {item.success_rate}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

          </>
        )}

        {/* Footer */}
        <div className="text-center space-y-2">
          <p className="text-xs text-muted-foreground">
            Performance metrics calculated from {totalTrades} evaluated trades in Trade History
          </p>
          <Link to="/ai-history">
            <Button variant="ghost" className="text-muted-foreground hover:text-foreground gap-2">
              <History className="h-4 w-4" />
              View Full Trade History
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
